import { Component, Input, computed, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { 
  RegistrationStepService, 
  RegistrationStepResponse, 
  RegistrationStep,
  RegistrationStatus 
} from '../../../core/services/registration-step.service';
import { UserSessionService } from '../../../core/services/mock-auth.service';
import { ProfessionalsDataService } from '../../services/professionals-data.service';
import { ProfessionalsService } from '../../../core/services/professionals.service';
import { ProfessionalsWizardService } from '../../services/professionals-wizard.service';
import { interval, Subscription, forkJoin } from 'rxjs';
import { switchMap, startWith } from 'rxjs/operators';

interface StepDisplay {
  step: RegistrationStep;
  label: string;
  status: RegistrationStatus;
  isCurrent: boolean;
  comments: string | null;
  updatedAt: Date | null;
}

@Component({
  selector: 'app-professionals-progression',
  standalone: true,
  imports: [CommonModule, CardModule, ButtonModule, ToastModule],
  providers: [MessageService],
  templateUrl: './professionals-progression.component.html',
  styleUrl: './professionals-progression.component.scss',
})
export class ProfessionalsProgressionComponent implements OnInit, OnDestroy {
  private messageService = inject(MessageService);
  private router = inject(Router);
  private registrationStepService = inject(RegistrationStepService);
  private mockAuthService = inject(UserSessionService);
  private professionalsDataService = inject(ProfessionalsDataService);
  private professionalsService = inject(ProfessionalsService);
  private professionalsWizardService = inject(ProfessionalsWizardService);

  // Professional ID - può essere passato come input o recuperato da demo session
  @Input() professionalId: string | null = null;

  // Expose enum for template
  readonly RegistrationStep = RegistrationStep;

  // Current step data from API
  currentStep = signal<RegistrationStepResponse | null>(null);
  
  // Step history for completion dates
  stepHistory = signal<RegistrationStepResponse[]>([]);
  
  isLoading = signal<boolean>(true);
  hasError = signal<boolean>(false);

  // Polling subscription for real-time updates
  private pollingSubscription?: Subscription;

  // All steps definition
  allSteps: Array<{ step: RegistrationStep; label: string }> = [
    { step: RegistrationStep.PORTAL_ACCESS, label: 'Primo accesso al portale' },
    { step: RegistrationStep.DATA_INPUT, label: 'Inserimento dati obbligatori per completare la domanda' },
    { step: RegistrationStep.VERIFICATION, label: 'Domanda in attesa di verifica' },
    { step: RegistrationStep.MFA_SETUP, label: 'Compilazione dati MFA (codici SOL)' },
    { step: RegistrationStep.CONTRACT_SENT, label: 'In attesa di contratto' },
    { step: RegistrationStep.CONTRACT_SIGNATURE, label: 'In attesa della tua firma sul contratto' },
    { step: RegistrationStep.CONTRACT_COUNTERSIGNATURE, label: 'In attesa di attivazione contratto' },
  ];

  // Computed display steps with status
  steps = computed<StepDisplay[]>(() => {
    const current = this.currentStep();
    const history = this.stepHistory();
    if (!current) return [];

    // Indice dello step corrente nella lista visualizzata. CONTRACT_ACTIVATED è lo stato
    // finale del flusso (BE: riga isCurrent=true, status COMPLETED) ma NON è tra gli step
    // elencati: senza questa gestione findIndex tornerebbe -1 e tutti gli step (anagrafica,
    // MFA, contratto) risulterebbero PENDING/grigi anche a registrazione conclusa. In quel
    // caso il flusso è tutto concluso, quindi trattiamo l'indice come oltre l'ultimo step.
    let currentIndex = this.allSteps.findIndex(s => s.step === current.step);
    if (currentIndex === -1 && current.step === RegistrationStep.CONTRACT_ACTIVATED) {
      currentIndex = this.allSteps.length;
    }

    return this.allSteps.map((stepDef) => {
      const stepIndex = this.allSteps.findIndex(s => s.step === stepDef.step);

      // Determine status for each step
      let status: RegistrationStatus;
      let isCurrent = false;
      let comments = null;
      let updatedAt = null;

      if (stepIndex < currentIndex) {
        // Steps before current are completed - get date from history
        status = RegistrationStatus.COMPLETED;
        
        // Find the last completed entry for this step in history
        const historyEntry = history
          .filter(h => h.step === stepDef.step && h.status === RegistrationStatus.COMPLETED)
          .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];
        
        if (historyEntry) {
          updatedAt = historyEntry.completedAt 
            ? new Date(historyEntry.completedAt) 
            : new Date(historyEntry.updatedAt);
        }
      } else if (stepIndex === currentIndex) {
        // Current step
        status = current.status;
        isCurrent = current.isCurrent;
        comments = current.comments;
        updatedAt = current.updatedAt ? new Date(current.updatedAt) : null;
      } else {
        // Steps after current are pending
        status = RegistrationStatus.PENDING;
      }

      return {
        step: stepDef.step,
        label: stepDef.label,
        status,
        isCurrent,
        comments,
        updatedAt,
      };
    });
  });

  // Computed: Check if there's a modification request in history
  hasModificationRequest = computed(() => {
    const current = this.currentStep();
    const history = this.stepHistory();
    
    if (!current) return false;
    
    // Only show banner if:
    // 1. Current step is DATA_INPUT with USER_INPUT status
    // 2. There's a VERIFICATION step with MODIFICATION_REQUESTED in history
    if (current.step === RegistrationStep.DATA_INPUT && current.status === RegistrationStatus.USER_INPUT) {
      return history.some(s =>
        s.step === RegistrationStep.VERIFICATION &&
        s.status === RegistrationStatus.MODIFICATION_REQUESTED
      );
    }

    // Fase MFA: modifica richiesta dal Back Office sui dati MFA (codici SOL).
    if (current.step === RegistrationStep.MFA_SETUP && current.status === RegistrationStatus.USER_INPUT) {
      return history.some(s =>
        s.step === RegistrationStep.MFA_SETUP &&
        s.status === RegistrationStatus.MODIFICATION_REQUESTED
      );
    }

    return false;
  });

  // Computed: la modifica è richiesta sui dati MFA (codici SOL) e non sull'anagrafica.
  // Serve solo al progression stepper per differenziare testo/azione del banner.
  isMfaModification = computed(() => {
    const current = this.currentStep();
    return current?.step === RegistrationStep.MFA_SETUP;
  });

  // Computed: Get modification comment
  modificationComment = computed(() => {
    const history = this.stepHistory();
    const current = this.currentStep();

    // In fase MFA usa la nota di modifica sullo step MFA_SETUP; altrimenti quella su VERIFICATION.
    const targetStep = current?.step === RegistrationStep.MFA_SETUP
      ? RegistrationStep.MFA_SETUP
      : RegistrationStep.VERIFICATION;

    // Find the most recent MODIFICATION_REQUESTED for the target step
    const modificationSteps = history
      .filter(s => s.step === targetStep && s.status === RegistrationStatus.MODIFICATION_REQUESTED)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    if (modificationSteps.length > 0) {
      // Parse "Modification requested: actual message" format
      const comment = modificationSteps[0].comments || '';
      return comment.replace(/^Modification requested:\s*/, '').trim();
    }
    
    return null;
  });

  // Signal for expand/collapse state of modification notes
  isModificationNotesExpanded = signal<boolean>(false);
  private readonly MAX_COMMENT_LENGTH = 150;

  // Computed: Check if comment is long
  isCommentLong = computed(() => {
    const comment = this.modificationComment();
    return comment ? comment.length > this.MAX_COMMENT_LENGTH : false;
  });

  // Computed: Get truncated comment
  displayedComment = computed(() => {
    const comment = this.modificationComment();
    if (!comment) return null;
    
    if (this.isCommentLong() && !this.isModificationNotesExpanded()) {
      return comment.substring(0, this.MAX_COMMENT_LENGTH) + '...';
    }
    
    return comment;
  });

  // Toggle modification notes expansion
  toggleModificationNotes(): void {
    this.isModificationNotesExpanded.update(expanded => !expanded);
  }

  // Active step for toast message
  activeStep = computed(() => {
    const current = this.currentStep();
    if (!current) return null;
    return this.allSteps.find(s => s.step === current.step);
  });

  // Live region message for screen readers
  liveRegionMessage = computed(() => {
    const current = this.currentStep();
    if (!current) return '';
    
    const stepLabel = this.getStepLabel(current.step, current.status);
    const subtitle = this.getStepSubtitle({
      step: current.step,
      label: stepLabel,
      status: current.status,
      isCurrent: current.isCurrent,
      comments: current.comments,
      updatedAt: current.updatedAt ? new Date(current.updatedAt) : null,
    });
    
    return `Step corrente: ${stepLabel}. ${subtitle}`;
  });

  ngOnInit() {
    // Use demo ID from login if not provided as input
    if (!this.professionalId) {
      const demoId = this.mockAuthService.getDemoProfessionalId();
      if (demoId) {
        this.professionalId = demoId;
        console.log(`[ProfessionalsProgression] Using demo ID from session: ${demoId}`);
      }
    }

    // Verify we have a professional ID before proceeding
    if (!this.professionalId) {
      console.warn('[ProfessionalsProgression] No professional ID available');
      this.hasError.set(true);
      this.isLoading.set(false);
      this.messageService.add({
        severity: 'warn',
        summary: 'ID Mancante',
        detail: 'Nessun ID fiduciario trovato. Effettua prima l\'accesso dal login oppure completa la pre-registrazione.',
        life: 5000,
      });
      // Redirect to pre-registration after showing the message
      setTimeout(() => {
        this.router.navigate(['/axa/pre-registration']).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }, 2000);
      return;
    }

    this.loadCurrentStep();
    this.startPolling();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  /**
   * Load current step and history from API
   */
  private loadCurrentStep() {
    this.isLoading.set(true);
    this.hasError.set(false);

    // Load both current step and history in parallel
    forkJoin({
      current: this.registrationStepService.getCurrentStep(this.professionalId),
      history: this.registrationStepService.getStepHistory(this.professionalId)
    }).subscribe({
      next: ({ current, history }) => {
        this.currentStep.set(current);
        this.stepHistory.set(history);
        this.isLoading.set(false);
        console.log('✅ Step corrente caricato:', current);
        console.log('✅ Storico step caricato:', history);
      },
      error: (err) => {
        console.error('❌ Errore caricamento step:', err);
        this.isLoading.set(false);
        this.hasError.set(true);
        
        // Provide more specific error message
        let errorDetail = 'Impossibile caricare lo stato della registrazione.';
        if (err.status === 404) {
          errorDetail = `Nessun dato di registrazione trovato per ID: ${this.professionalId}. Completa prima la pre-registrazione.`;
        } else if (err.status === 0) {
          errorDetail = 'Impossibile connettersi al server. Verifica la connessione.';
        }
        
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: errorDetail,
          life: 5000,
        });
      },
    });
  }

  /**
   * Start polling for step updates every 30 seconds
   */
  private startPolling() {
    // Poll every 30 seconds to check for updates (without initial call)
    this.pollingSubscription = interval(30000)
      .pipe(
        switchMap(() => forkJoin({
          current: this.registrationStepService.getCurrentStep(this.professionalId),
          history: this.registrationStepService.getStepHistory(this.professionalId)
        }))
      )
      .subscribe({
        next: ({ current, history }) => {
          const previousStep = this.currentStep();
          this.currentStep.set(current);
          this.stepHistory.set(history);
          
          // If step changed, log it
          if (!previousStep || previousStep.step !== current.step || previousStep.status !== current.status) {
            console.log('🔄 Step aggiornato:', current);
          }
        },
        error: (err) => {
          console.warn('⚠️ Polling error:', err);
          // Don't show error toast for polling failures - user can refresh manually
        },
      });
  }

  /**
   * Stop polling on component destroy
   */
  private stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  /**
   * Update toast message based on current step
   * NOTE: Toast disabled - status is visible in the UI
   */
  private updateToast() {
    // Toast removed - user can see status directly in the progression UI
    return;
  }

  /**
   * Check if step is completed
   */
  isCompleted(step: RegistrationStep): boolean {
    const stepDisplay = this.steps().find(s => s.step === step);
    return stepDisplay?.status === RegistrationStatus.COMPLETED;
  }

  /**
   * Check if step is active (current)
   */
  isActive(step: RegistrationStep): boolean {
    const stepDisplay = this.steps().find(s => s.step === step);
    return stepDisplay?.isCurrent === true;
  }

  /**
   * Check if step requires user input
   */
  requiresInput(step: RegistrationStep): boolean {
    const stepDisplay = this.steps().find(s => s.step === step);
    return stepDisplay?.status === RegistrationStatus.USER_INPUT;
  }

  /**
   * Check if step is rejected
   */
  isRejected(step: RegistrationStep): boolean {
    const stepDisplay = this.steps().find(s => s.step === step);
    return stepDisplay?.status === RegistrationStatus.REJECTED;
  }

  /**
   * Get status color for step
   */
  getStatusColor(step: RegistrationStep): string {
    const stepDisplay = this.steps().find(s => s.step === step);
    if (!stepDisplay) return '#9CA3AF'; // Default grey
    return this.registrationStepService.getStatusColor(stepDisplay.status);
  }

  /**
   * Get status icon for step
   */
  getStatusIcon(step: RegistrationStep): string {
    const stepDisplay = this.steps().find(s => s.step === step);
    if (!stepDisplay) return 'pi-clock';
    return this.registrationStepService.getStatusIcon(stepDisplay.status);
  }

  /**
   * Get dynamic label for step based on its status
   */
  getStepLabel(step: RegistrationStep, status: RegistrationStatus): string {
    // If completed, some steps change their title
    if (status === RegistrationStatus.COMPLETED) {
      switch (step) {
        case RegistrationStep.VERIFICATION:
          return 'Domanda verificata';
        case RegistrationStep.MFA_SETUP:
          return 'Dati MFA validati';
        case RegistrationStep.CONTRACT_SENT:
          return 'Contratto inviato';
        case RegistrationStep.CONTRACT_SIGNATURE:
          return 'Contratto firmato';
        case RegistrationStep.CONTRACT_COUNTERSIGNATURE:
          return 'Contratto attivato';
      }
    }
    
    // Default labels from allSteps
    const defaultLabel = this.allSteps.find(s => s.step === step)?.label;
    return defaultLabel || step;
  }

  /**
   * Get subtitle text for step based on status and timestamps
   */
  getStepSubtitle(stepDisplay: StepDisplay): string {
    const { status, isCurrent, updatedAt, step } = stepDisplay;

    // If step is completed, show completion date/time
    if (status === RegistrationStatus.COMPLETED && updatedAt) {
      // Special case for DATA_INPUT
      if (step === RegistrationStep.DATA_INPUT) {
        return `Anagrafica inviata - ${this.formatDateTime(updatedAt)}`;
      }
      return `Completato - ${this.formatDateTime(updatedAt)}`;
    }

    // If current step and requires action (USER_INPUT or REJECTED)
    if (isCurrent && (status === RegistrationStatus.USER_INPUT || status === RegistrationStatus.REJECTED)) {
      return 'In corso - Azione richiesta';
    }

    // If rejected (not current)
    if (status === RegistrationStatus.REJECTED) {
      return 'Respinto';
    }

    // If pending
    if (status === RegistrationStatus.PENDING) {
      // Fase MFA: quando lo step è quello corrente ed è in PENDING, il fiduciario ha
      // inviato i dati MFA e attende la validazione del Back Office (coerente con la
      // fase anagrafica, dove dopo l'invio si passa a "Domanda in attesa di verifica").
      if (step === RegistrationStep.MFA_SETUP && isCurrent) {
        return 'In attesa di validazione del Back Office';
      }
      return 'Non ancora disponibile';
    }

    // Default fallback
    return 'In attesa';
  }

  /**
   * Format date and time for display
   */
  private formatDateTime(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} alle ${hours}:${minutes}`;
  }

  /**
   * Get accessible label for action button
   */
  getActionButtonLabel(stepDisplay: StepDisplay): string {
    const stepLabel = this.getStepLabel(stepDisplay.step, stepDisplay.status);
    const action = stepDisplay.status === RegistrationStatus.REJECTED ? 'Correggi' : 'Riprendi';
    return `${action} lo step: ${stepLabel}`;
  }

  /**
   * Navigate to action based on current step
   */
  continueToStep(step: RegistrationStep) {
    const current = this.currentStep();
    if (!current || current.step !== step) {
      console.warn('Can only continue from current step');
      return;
    }

    // Navigate based on step type
    switch (step) {
      case RegistrationStep.DATA_INPUT:
        // If operational location, navigate directly to specific data (no :id needed)
        if (this.professionalsWizardService.isOperationalLocationOnboarding()) {
          this.router.navigate(['/axa/professionals-specific-data']).then(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
        } else {
          // Navigate directly to first step of data input
          this.router.navigate(['/axa/professionals-general-data']).then(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
        }
        break;
      case RegistrationStep.VERIFICATION:
        // Show verification status or allow edits if rejected
        if (current.status === RegistrationStatus.REJECTED) {
          this.router.navigate(['/axa/professionals-general-data']).then(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
        }
        break;
      case RegistrationStep.MFA_SETUP:
        // Fase MFA: il fiduciario compila i dati MFA (codici SOL) solo quando
        // il Back Office ha aperto la compilazione (USER_INPUT). In PENDING è in
        // attesa di richiesta/revisione del Back Office (nessuna azione).
        if (current.status === RegistrationStatus.USER_INPUT) {
          this.router.navigate(['/axa/professionals-mfa']).then(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
        }
        break;
      case RegistrationStep.CONTRACT_SENT:
        // View contract or wait
        console.log('Contract step - no action yet');
        break;
      case RegistrationStep.CONTRACT_SIGNATURE:
        // Navigate to contract signature page
        this.router.navigate(['/axa/professionals-contracts']).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
        break;
      case RegistrationStep.CONTRACT_COUNTERSIGNATURE:
        // Contract is awaiting AXA countersignature - no action available for professional
        console.log('Contract countersignature step - waiting for AXA action');
        break;
      default:
        console.log(`No action defined for step: ${step}`);
    }
  }

  /**
   * Manually refresh current step
   */
  refreshStep() {
    this.loadCurrentStep();
  }

  /**
   * Il fiduciario può SEMPRE (sola lettura) visualizzare l'anagrafica una volta inviata,
   * anche quando si trova su step successivi (verifica, MFA, contratto). L'anagrafica risulta
   * inviata dallo step VERIFICATION in poi (incluso MFA_SETUP): in DATA_INPUT è ancora in
   * compilazione/modifica e la si apre normalmente in scrittura.
   */
  canViewAnagrafica = computed(() => {
    const current = this.currentStep();
    return current?.step === RegistrationStep.VERIFICATION ||
           current?.step === RegistrationStep.MFA_SETUP ||
           current?.step === RegistrationStep.CONTRACT_SENT ||
           current?.step === RegistrationStep.CONTRACT_SIGNATURE ||
           current?.step === RegistrationStep.CONTRACT_COUNTERSIGNATURE;
  });

  /**
   * Il fiduciario può SEMPRE (sola lettura) rivedere il riepilogo dei dati MFA (codici SOL)
   * una volta inviati: in MFA_SETUP con stato PENDING (dati inviati, in attesa di validazione
   * del Back Office) e in tutti gli step successivi al MFA. Durante la compilazione/modifica
   * (MFA_SETUP + USER_INPUT) si usa invece il normale accesso in scrittura.
   *
   * ATTENZIONE: in MFA_SETUP lo stato PENDING ha due significati distinti:
   *  - PENDING iniziale (subito dopo l'approvazione dell'anagrafica, comments
   *    "In attesa di richiesta compilazione dati MFA"): il Back Office non ha ancora
   *    richiesto la compilazione e NULLA è stato inviato → il riepilogo NON deve comparire.
   *  - PENDING post-invio: il fiduciario ha confermato i dati (transizione
   *    USER_INPUT → PENDING) ed è in attesa di validazione → il riepilogo va mostrato.
   * Per distinguerli si verifica nello storico la presenza di una voce
   * MFA_SETUP + USER_INPUT: esiste solo se la compilazione è stata effettivamente aperta
   * e quindi i dati sono stati inviati almeno una volta.
   */
  canViewMfaRecap = computed(() => {
    const current = this.currentStep();
    if (!current) return false;
    if (current.step === RegistrationStep.MFA_SETUP) {
      return current.status === RegistrationStatus.PENDING &&
             this.stepHistory().some(s =>
               s.step === RegistrationStep.MFA_SETUP &&
               s.status === RegistrationStatus.USER_INPUT
             );
    }
    return current.step === RegistrationStep.CONTRACT_SENT ||
           current.step === RegistrationStep.CONTRACT_SIGNATURE ||
           current.step === RegistrationStep.CONTRACT_COUNTERSIGNATURE;
  });

  /**
   * Naviga alla pagina MFA in sola lettura. La pagina rileva automaticamente che lo step
   * non è editabile e mostra il riepilogo dei dati MFA inviati.
   */
  viewMfaRecap() {
    this.router.navigate(['/axa/professionals-mfa']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.messageService.add({
        severity: 'info',
        summary: 'Modalità Sola Lettura',
        detail: 'Stai visualizzando il riepilogo dei dati MFA inviati in modalità sola lettura',
        life: 4000,
      });
    });
  }

  /**
   * Navigate to view submitted registration data in read-only mode
   * Read-only mode is automatically activated for VERIFICATION, CONTRACT_SENT, CONTRACT_SIGNATURE, or CONTRACT_COUNTERSIGNATURE steps
   */
  viewSubmittedData() {
    console.log('[ProfessionalsProgression] Navigating to view submitted data');
    
    // Navigate to general data (first step of wizard)
    // Read-only mode will be automatically active since we're in VERIFICATION/CONTRACT step
    this.router.navigate(['/axa/professionals-general-data']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      this.messageService.add({
        severity: 'info',
        summary: 'Modalità Sola Lettura',
        detail: 'Stai visualizzando l\'anagrafica inviata in modalità sola lettura',
        life: 4000,
      });
    });
  }

  /** Naviga alla pagina del contratto per scaricare il documento finale controfirmato */
  goToContracts() {
    this.router.navigate(['/axa/professionals-contracts']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  goToPreRegistration() {
    this.router.navigate(['/axa/pre-registration']).then(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}
